// src/business/components/BusinessSidebar.jsx
import { Link, NavLink, useLocation } from "react-router-dom";
import { Bike, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useBusinessAuth } from "../context/BusinessAuthContext";
import { useBusinessMessaging } from "../context/BusinessMessagingContext";
import { Counter, IconButton, cn, focusRing } from "../../components/ui";
import {
  DASHBOARD_LINK,
  NAV_GROUPS,
  SALES_ANALYTICS_LINK,
  businessRiderNavigation,
  isGroupActive,
  isItemActive,
  navigationAccess,
  resolveItemTo,
} from "../navigation/businessNavigation";

// The navigation map lives in ../navigation/businessNavigation.js; this
// re-export keeps the existing import path for the topbar and rider pages.
export { businessRiderNavigation };

/* ─── Styles (DESIGN.md › Layout › Business Portal shell) ───────────── */

const row = "flex min-h-8 items-center gap-2.5 rounded-control px-2 text-body-sm transition-colors duration-150 max-md:min-h-touch";

const topLinkClass = ({ isActive }) =>
  cn(row, focusRing, isActive ? "bg-accent-subtle font-medium text-accent" : "text-secondary hover:bg-hover hover:text-foreground");

const groupButtonClass = (active) =>
  cn(row, "w-full font-medium", focusRing, active ? "text-foreground" : "text-secondary hover:bg-hover hover:text-foreground");

// Selected: the accent tint plus a 2 px mark on the group's guide line.
const subLinkClass = (active) =>
  cn(
    "relative flex min-h-8 items-center gap-2 rounded-control pl-3 pr-2 text-body-sm transition-colors duration-150 max-md:min-h-touch",
    focusRing,
    active
      ? "bg-accent-subtle font-medium text-accent before:absolute before:-left-[9px] before:bottom-1.5 before:top-1.5 before:w-0.5 before:rounded-full before:bg-accent"
      : "text-secondary hover:bg-hover hover:text-foreground"
  );

/* ─── Sidebar ────────────────────────────────────────────────────────── */

/**
 * BusinessSidebar — the portal's task map. `onClose` is passed when it is
 * shown in the mobile navigation sheet.
 */
export default function BusinessSidebar({ onNavigate, onClose }) {
  const auth = useBusinessAuth?.() || {};
  const riderNavigation = businessRiderNavigation(auth);
  const businessName = auth.businessName || null;
  const { can, canSee } = navigationAccess(auth);
  const { unreadTotal = 0 } = useBusinessMessaging();
  const displayUnreadTotal = unreadTotal > 99 ? "99+" : unreadTotal;
  const location = useLocation();

  const groupActive = useCallback((group) => isGroupActive(group, location.pathname), [location.pathname]);
  const itemActive = (item, to) => isItemActive(item, to, location);

  const unreadCounter = () => (
    <Counter tone="accent" aria-label={`${displayUnreadTotal} unread business messages`}>
      {displayUnreadTotal}
    </Counter>
  );

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    NAV_GROUPS.forEach((group) => { initial[group.label] = groupActive(group); });
    return initial;
  });

  // Moving into a workspace opens its group and closes the one left behind.
  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      NAV_GROUPS.forEach((group) => {
        const shouldStayOpen = groupActive(group);
        if (Boolean(next[group.label]) !== shouldStayOpen) {
          next[group.label] = shouldStayOpen;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [groupActive]);

  const toggle = (label) => setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  const DashboardIcon = DASHBOARD_LINK.icon;
  const SalesAnalyticsIcon = SALES_ANALYTICS_LINK.icon;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Business identity — the same height as the topbar so their rules meet. */}
      <div className="flex h-[var(--layout-topbar)] shrink-0 items-center gap-2 border-b border-subtle px-4">
        <div className="min-w-0 flex-1">
          <div className="truncate text-panel-title text-foreground">{businessName || "—"}</div>
          <div className="text-meta text-tertiary">Business portal</div>
        </div>
        {onClose ? <IconButton icon={X} label="Close menu" size="sm" onClick={onClose} className="-mr-2" /> : null}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto px-2 py-3 scrollbar-fade" aria-label="Business navigation">
        <NavLink to={DASHBOARD_LINK.to} end={DASHBOARD_LINK.end} className={topLinkClass} onClick={onNavigate}>
          <DashboardIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{riderNavigation.riderOnly ? "Work home" : DASHBOARD_LINK.label}</span>
        </NavLink>

        {canSee(SALES_ANALYTICS_LINK) && (
          <NavLink to={SALES_ANALYTICS_LINK.to} className={topLinkClass} onClick={onNavigate}>
            <SalesAnalyticsIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{SALES_ANALYTICS_LINK.label}</span>
          </NavLink>
        )}

        {riderNavigation.canEnter && (
          <NavLink to="/rider" state={{ riderBusinessId: auth.businessId }} className={topLinkClass} onClick={onNavigate}>
            <Bike className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">Open rider workspace</span>
          </NavLink>
        )}

        <div className="mt-3 flex flex-col gap-px">
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(canSee);
            if (visibleItems.length === 0) return null;
            const active = groupActive(group);
            const open = Boolean(openGroups[group.label]);
            const GroupIcon = group.icon;
            const submenuId = `business-nav-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

            return (
              <div key={group.label} role="group" aria-label={group.label}>
                <button
                  type="button"
                  onClick={() => toggle(group.label)}
                  aria-expanded={open}
                  aria-controls={submenuId}
                  className={groupButtonClass(active)}
                >
                  <GroupIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1 truncate text-left">{group.label}</span>
                  {group.showsUnread && !open && unreadTotal > 0 && unreadCounter()}
                  <ChevronRight
                    className={cn("h-3.5 w-3.5 shrink-0 text-tertiary transition-transform duration-150", open && "rotate-90")}
                    aria-hidden="true"
                  />
                </button>

                {open && (
                  <div id={submenuId} className="mb-1 ml-4 mt-px flex flex-col gap-px border-l border-subtle pl-2">
                    {visibleItems.map((item) => {
                      const to = resolveItemTo(item, can);
                      const activeItem = itemActive(item, to);
                      return (
                        <Link
                          key={item.label}
                          to={to}
                          aria-current={activeItem ? "page" : undefined}
                          className={subLinkClass(activeItem)}
                          onClick={onNavigate}
                        >
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                          {item.showsUnread && unreadTotal > 0 && unreadCounter()}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
